import React, { useEffect, useRef, useState } from "react";
import { MoreVertical, Trash2, Copy, Pencil, Plus, Move } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const GRID = 10;
const snap = (n) => Math.round(n / GRID) * GRID;

export default function SheetBox({ box, onChange, onClone, onDelete, onRename, canvasRef, isIdentity, editMode, readOnly }) {
  const [drag, setDrag] = useState(null);
  const [resize, setResize] = useState(null);
  const nodeRef = useRef(null);

  const startDrag = (e) => {
    if (readOnly) return;
    if (e.target.closest("[data-no-drag]")) return;
    e.preventDefault();
    setDrag({ startX: e.clientX, startY: e.clientY, x: box.x, y: box.y });
  };

  const startResize = (e) => {
    if (readOnly) return;
    e.preventDefault(); e.stopPropagation();
    setResize({ startX: e.clientX, startY: e.clientY, w: box.w, h: box.h });
  };

  useEffect(() => {
    if (readOnly) return undefined;
    if (!drag && !resize) return undefined;
    const move = (e) => {
      if (drag) {
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        onChange({ ...box, x: Math.max(0, snap(drag.x + dx)), y: Math.max(0, snap(drag.y + dy)) });
      } else if (resize) {
        const dx = e.clientX - resize.startX;
        const dy = e.clientY - resize.startY;
        onChange({ ...box, w: Math.max(160, snap(resize.w + dx)), h: Math.max(120, snap(resize.h + dy)) });
      }
    };
    const up = () => { setDrag(null); setResize(null); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [drag, resize, box, onChange, readOnly]);

  const setRow = (idx, patch) => {
    const rows = [...(box.rows || [])];
    rows[idx] = { ...rows[idx], ...patch };
    onChange({ ...box, rows });
  };
  const addRow = () => {
    const rows = [...(box.rows || []), { value: "", label: "" }];
    onChange({ ...box, rows });
  };
  const removeRow = (idx) => {
    const rows = [...(box.rows || [])];
    rows.splice(idx, 1);
    onChange({ ...box, rows });
  };
  const renameInPlace = () => {
    const next = window.prompt("Rename box", box.title);
    if (next && next.trim()) onRename(next.trim());
  };

  return (
    <div
      ref={nodeRef}
      className={`absolute group rounded-lg border bg-[#0d1320]/95 backdrop-blur-sm shadow-lg transition-colors ${
        readOnly ? "border-[#212A3E]" : (editMode ? "border-[#D4AF37]/50" : "border-[#212A3E] hover:border-[#D4AF37]/50")
      }`}
      style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
      data-testid={`box-${box.type}`}
    >
      {/* Header */}
      <div
        onMouseDown={startDrag}
        className={`flex items-center justify-between border-b border-[#212A3E] px-3 py-2 select-none ${readOnly ? "" : "cursor-move"}`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {!readOnly && <Move className="h-3.5 w-3.5 shrink-0 text-[#D4AF37]/60" />}
          {editMode && !readOnly ? (
            <input
              value={box.title}
              onChange={(e) => onChange({ ...box, title: e.target.value })}
              className="min-w-0 flex-1 border-b border-[#D4AF37]/40 bg-transparent font-serif-display text-lg text-white outline-none focus:border-[#D4AF37]"
              data-no-drag
              data-testid={`box-${box.type}-title-input`}
              placeholder="Box title"
            />
          ) : (
            <span className="font-serif-display text-lg text-white">{box.title}</span>
          )}
        </div>
        {!readOnly && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild data-no-drag>
            <button
              className="rounded p-1 text-[#A0ABC0] hover:bg-white/5 hover:text-white"
              data-testid={`box-${box.type}-menu`}
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border-[#212A3E] bg-[#101623] text-white">
            <DropdownMenuItem onClick={renameInPlace} data-testid="box-menu-rename">
              <Pencil className="mr-2 h-4 w-4" />Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onClone} data-testid="box-menu-clone">
              <Copy className="mr-2 h-4 w-4" />Clone
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#212A3E]" />
            <DropdownMenuItem onClick={onDelete} className="text-red-400 focus:text-red-300" data-testid="box-menu-delete">
              <Trash2 className="mr-2 h-4 w-4" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>

      {/* Body */}
      <div className="overflow-auto px-3 py-3" style={{ height: box.h - 44 }} data-no-drag>
        {isIdentity ? <IdentityBody box={box} onChange={onChange} editMode={editMode} readOnly={readOnly} /> : <RowsBody box={box} setRow={setRow} addRow={addRow} removeRow={removeRow} editMode={editMode} readOnly={readOnly} />}
      </div>

      {/* Resize handle — hidden in read-only mode */}
      {!readOnly && (
      <div
        onMouseDown={startResize}
        className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize"
        data-testid={`box-${box.type}-resize`}
        style={{
          background:
            "linear-gradient(135deg, transparent 50%, rgba(212,175,55,0.6) 50%, rgba(212,175,55,0.6) 60%, transparent 60%, transparent 70%, rgba(212,175,55,0.6) 70%, rgba(212,175,55,0.6) 80%, transparent 80%)",
        }}
        data-no-drag
      />
      )}
    </div>
  );
}

function IdentityBody({ box, onChange, editMode, readOnly }) {
  const setField = (i, patch) => {
    const fields = [...box.fields];
    fields[i] = { ...fields[i], ...patch };
    onChange({ ...box, fields });
  };
  const addField = () => {
    const fields = [...box.fields, { label: "Label", value: "", custom: true }];
    onChange({ ...box, fields });
  };
  const removeField = (i) => {
    const fields = [...box.fields];
    fields.splice(i, 1);
    onChange({ ...box, fields });
  };
  const setFull = (v) => onChange({ ...box, fullWidthField: { ...box.fullWidthField, value: v } });
  const setFullLabel = (v) => onChange({ ...box, fullWidthField: { ...box.fullWidthField, label: v } });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
        {box.fields.map((f, i) => {
          const labelEditable = !readOnly && (editMode || f.custom);
          return (
            <div key={i} className="group/field flex items-baseline gap-2">
              {labelEditable ? (
                <input
                  value={f.label}
                  onChange={(e) => setField(i, { label: e.target.value })}
                  className="font-mono-tight w-16 shrink-0 bg-transparent text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] outline-none focus:text-[#F0E68C]"
                  aria-label="Field label"
                  data-testid={`identity-label-${i}`}
                />
              ) : (
                <span className="font-mono-tight w-16 shrink-0 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]">{f.label}</span>
              )}
              <input
                className={`fillable ${f.num ? "fillable-num !w-20" : ""}`}
                value={f.value} onChange={(e) => setField(i, { value: e.target.value })}
                readOnly={readOnly}
                data-testid={`identity-${(f.label || "field").toLowerCase()}-input`}
              />
              {labelEditable && (
                <button
                  onClick={() => removeField(i)}
                  className="opacity-0 transition-opacity group-hover/field:opacity-100 text-[#A0ABC0] hover:text-red-400"
                  aria-label="Remove line"
                  data-no-drag
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {!readOnly && (
      <button
        onClick={addField}
        className="flex items-center gap-1 text-xs text-[#D4AF37]/80 hover:text-[#D4AF37]"
        data-no-drag
        data-testid="identity-add-line"
      >
        <Plus className="h-3 w-3" /> Add line
      </button>
      )}
      <label className="block">
        {editMode && !readOnly ? (
          <input
            value={box.fullWidthField.label}
            onChange={(e) => setFullLabel(e.target.value)}
            className="font-mono-tight bg-transparent text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] outline-none focus:text-[#F0E68C]"
            aria-label="Full-width line label"
          />
        ) : (
          <span className="font-mono-tight text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]">{box.fullWidthField.label}</span>
        )}
        <input
          className="fillable mt-1"
          value={box.fullWidthField.value} onChange={(e) => setFull(e.target.value)}
          readOnly={readOnly}
          data-testid="identity-destiny-input" placeholder="Who do I want to be? What is the dream that I must pursue to die happy?"
        />
      </label>
    </div>
  );
}

function RowsBody({ box, setRow, addRow, removeRow, editMode, readOnly }) {
  // Stats-like (label fixed + number) vs custom-label rows vs single value
  const isStats = box.type === "stats";
  const isNumeric = isStats || box.type === "skills";
  const hasLabel = isStats || box.type === "skills" || box.type === "custom";
  const showLabel = hasLabel;
  const showAdd = !readOnly && (box.allowAdd || editMode);
  const showRemove = !readOnly && (box.allowAdd || editMode);
  return (
    <div className="space-y-2">
      {(box.rows || []).map((r, i) => (
        <div key={i} className="flex items-center gap-2 group/row">
          {showLabel && (
            isStats && (!editMode || readOnly) ? (
              <span className="font-mono-tight w-20 shrink-0 text-[11px] uppercase tracking-[0.16em] text-[#A0ABC0]">{r.label}</span>
            ) : (
              <input
                className={isStats
                  ? "font-mono-tight w-20 shrink-0 bg-transparent text-[11px] uppercase tracking-[0.16em] text-[#A0ABC0] outline-none focus:text-white border-b border-[#D4AF37]/30 focus:border-[#D4AF37]"
                  : "fillable flex-1"}
                value={r.label || ""} onChange={(e) => setRow(i, { label: e.target.value })}
                readOnly={readOnly}
                placeholder={isStats ? "Stat" : (box.type === "skills" ? "Skill name" : "Label")}
              />
            )
          )}
          <input
            className={`fillable ${isNumeric ? "fillable-num" : "flex-1"}`}
            value={r.value || ""} onChange={(e) => setRow(i, { value: e.target.value })}
            readOnly={readOnly}
            placeholder={isNumeric ? "0" : "—"}
          />
          {showRemove && (
            <button
              onClick={() => removeRow(i)}
              className="opacity-0 group-hover/row:opacity-100 text-[#A0ABC0] hover:text-red-400 transition-opacity"
              data-no-drag aria-label="remove row"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      {showAdd && (
        <button
          onClick={addRow}
          className="mt-1 flex items-center gap-1 text-xs text-[#D4AF37]/80 hover:text-[#D4AF37]"
          data-no-drag
          data-testid={`box-${box.type}-add-row`}
        >
          <Plus className="h-3 w-3" /> Add line
        </button>
      )}
    </div>
  );
}
